import React, { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { customerState, setCustomerData, setCustomerAddress } from '../store/slices/customer';
import { setProductData } from '../store/slices/product';
import { popupState, openPopup, closePopup } from '../store/slices/popup';
import { setTokenData } from '../store/slices/token';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import stateList from '../constants/state-list';
import regexPatterns from '../constants/regexPatterns';

import { useTranslation } from 'react-i18next';

import BasePage from './BasePage';
import { TextInput, AddressInput, Popup, SelectInput } from '../components';

import { documentValidation, whitespaceValidation } from '../helpers/validation';
import { getAddressByZipcode, getUserExistence, getUserData, postLeadData } from '../services/customer';


/**
* Page that holds user form
*/
const CustomerPage = () => {
  const { t } = useTranslation('customer');
  const dispatch = useDispatch();
  const { customer } = useSelector(customerState);
  const { currentPopup } = useSelector(popupState);
  const { address } = customer;
  const { REACT_APP_CONTACT_PHONE } = process.env;

  const [currentLoading, setLoaderOn] = useState('');
  const [isTyped, setTyped] = useState(false);
  const [customerStatus, setCustomerStatus] = useState('');
  const mustTalkToComercial = (customerStatus !== 'inactiveCustomer');

  const customerInputCallback = (name, value) => {
    dispatch(setCustomerData({
      ...customer,
      [name]: value
    }));
    setTyped(true);
  }

  const addressInputCallback = (name, value) => {
    dispatch(setCustomerAddress({
      ...address,
      [name]: value
    }));
    setTyped(true);
  }

  const passwordAsyncValidation = (password) => {
    setLoaderOn('password');
    return getUserData(customer.email, password).then((customerData) => {
      const { customer, product } = customerData;
      const isValidUser = !!customer?.id;

      if (isValidUser) {
        dispatch(setCustomerData({ ...customer, password }));
        dispatch(setProductData({ ...product }));
      }
      return isValidUser;
    })
      .catch(() => false)
      .finally(() => setLoaderOn(''));
  }

  useEffect(() => {
    dispatch(setCustomerData({ ...customer }));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (regexPatterns.email.test(customer.email) && isTyped) {
        setLoaderOn('email');
        getUserExistence(customer.email)
        .then(({ userExists, isSubscriptionActive }) => {
          if (userExists) {
            dispatch(openPopup('user-exists'));
            setCustomerStatus((isSubscriptionActive ? 'activeCustomer' : 'inactiveCustomer'));
          }
        })
        .catch(() => {
          dispatch(openPopup('user-exists'));
          setCustomerStatus(('errorCustomer'))
        })
        .finally(() => setLoaderOn(''));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [customer.email]);

  useEffect(() => {
    if (regexPatterns.zipcode.test(address.zipcode) && isTyped) {
      setLoaderOn('zipcode');
      getAddressByZipcode(address.zipcode).then((addressData) => {
        let data = (addressData?.city ? { ...address, ...addressData } : { zipcode: address.zipcode });
        dispatch(setCustomerAddress(data));
      })
        .finally(() => setLoaderOn(''));
    }
  }, [address.zipcode]);
  return (
    <>
      <Popup
        title={t(`existsPopup.${customerStatus}.title`, '')}
        text={t(`existsPopup.${customerStatus}.text`, { email: customer.email || '' })}
        isOpen={currentPopup == 'user-exists'}
        onSubmit={() => dispatch(closePopup())}
        primaryButton={{
          text: t(`existsPopup.${customerStatus}.renewButton`),
          type: mustTalkToComercial ? 'link' : 'submit',
          mode: 'primary',
          url: mustTalkToComercial ? `https://wa.me/55${REACT_APP_CONTACT_PHONE}` : ''
        }}
        secondaryButton={{
          text: t(`existsPopup.${customerStatus}.buyButton`),
          type: 'button',
          mode: 'secondary',
          onClick: () => window.location.reload(false)
        }}>
        {
          !mustTalkToComercial && <TextInput
            name='password'
            mode='password'
            label={t('existsPopup.password.label')}
            loading={currentLoading === 'password'}
            validation={{
              required: t('existsPopup.password.required'),
              validate: async (value) => await passwordAsyncValidation(value) || t('existsPopup.password.invalid')
            }}
          />
        }
      </Popup>
      <BasePage
        title={t('title')}
        navigation={{
          primary: {
            text: t('primaryButton'),
            path: '/product',
            onBeforePageChange: () => {
              postLeadData({ ...customer, ...address, conversionIdentifier: 'preencheu_dados_pessoais' })
            }
          }
        }}>
        <GoogleReCaptcha onVerify={(token) => dispatch(setTokenData(token))} />
        <div className='page__container'>
          <TextInput
            name='email'
            label={t('form.email.label')}
            width={70}
            keyboard='email'
            defaultValue={customer.email}
            loading={currentLoading === 'email'}
            validation={{
              required: t('form.email.required'),
              pattern: { value: regexPatterns.email, message: t('form.email.invalid') }
            }}
            onChange={customerInputCallback}
          />
          <TextInput
            name='phone'
            label={t('form.phone.label')}
            width={30}
            keyboard='numeric'
            defaultValue={customer.phone}
            validation={{
              required: t('form.phone.required'),
              pattern: { value: regexPatterns.phone, message: t('form.phone.invalid') }
            }}
            mask={[
              { pattern: '(00) 0000-0000', length: 0 },
              { pattern: '(00) 00000-0000', length: 11 }
            ]}
            onBlur={customerInputCallback}
          />
          <TextInput
            name='fullName'
            label={t('form.fullName.label')}
            width={60}
            defaultValue={customer.fullName}
            validation={{
              required: t('form.fullName.required'),
              validate: (value) => (whitespaceValidation(value) || t('form.fullName.invalid'))
            }}
            onBlur={customerInputCallback}
          />
          <TextInput
            name='document'
            label={t('form.document.label')}
            width={40}
            keyboard='numeric'
            defaultValue={customer.document}
            mask={
              [
                { pattern: '000.000.000-00', length: 0 },
                { pattern: '00.000.000/0000-00', length: 12 }
              ]
            }
            validation={{
              required: t('form.document.required'),
              validate: (value) => (documentValidation(value) || t('form.document.invalid'))
            }}
            onBlur={customerInputCallback}
          />
          <TextInput
            name='zipcode'
            label={t('form.zipcode.label')}
            width={25}
            keyboard='numeric'
            defaultValue={address.zipcode}
            loading={currentLoading === 'zipcode'}
            validation={{
              required: t('form.zipcode.required'),
              pattern: { value: regexPatterns.zipcode, message: t('form.zipcode.invalid') }
            }}
            mask={[{ pattern: '00000-000', length: 0 }]}
            onChange={addressInputCallback}
          />
          <AddressInput
            label={t('form.address.label')}
            width={75}
            loading={currentLoading === 'zipcode'}
            street={address.street}
            neighborhood={address.neighborhood}
            city={address.city}
            state={address.state}
            zipcode={address.zipcode}
            validation={{ required: t('form.address.required') }}
            onClick={() => dispatch(openPopup('addressPopup'))}
          />
        </div>
      </BasePage>
      <Popup
        title={t('addressPopup.title')}
        isOpen={currentPopup == 'addressPopup'}
        onSubmit={(data) => {
          dispatch(setCustomerAddress({ ...address, ...data }));
          dispatch(closePopup());
        }}
        primaryButton={{
          text: t('addressPopup.saveButton'),
          type: 'submit',
          mode: 'primary'
        }}
        secondaryButton={{
          text: t('addressPopup.backButton'),
          type: 'button',
          mode: 'secondary',
          onClick: () => dispatch(closePopup())
        }}>
        <TextInput
          name='street'
          label={t('form.address.label')}
          defaultValue={address.street}
          validation={{
            required: t('form.address.required'),
            validate: (value) => (whitespaceValidation(value) || t('form.address.invalid'))
          }}
        />
        <TextInput
          name='neighborhood'
          label={t('form.neighborhood.label')}
          defaultValue={address.neighborhood}
          validation={{
            required: t('form.neighborhood.required'),
            validate: (value) => (whitespaceValidation(value) || t('form.neighborhood.invalid'))
          }}
        />
        <TextInput
          name='city'
          label={t('form.city.label')}
          defaultValue={address.city}
          validation={{
            required: t('form.city.required'),
            validate: (value) => (whitespaceValidation(value) || t('form.city.invalid'))
          }}
        />
        <SelectInput
          name='state'
          label={t('form.state.label')}
          defaultValue={address.state}
          validation={{ required: t('form.state.required') }}
          onSelect={addressInputCallback}
          options={{
            key: 'uf',
            value: 'uf',
            text: 'name',
            data: stateList
          }}
        />
      </Popup>
    </>
  );
}
export default CustomerPage;